import React, { useEffect, useState } from 'react';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
    Modal,
    Button,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    IconButton,
    Stack,
    Pagination,
    Autocomplete,
    TextField,
} from '@mui/material';
import { ManageAccounts, Add, LockReset, ToggleOn, ToggleOff, Delete, Security, PhonelinkLock } from '@mui/icons-material';
import { UserType } from '../../interfaces/userType.enum';
import { IInstitution } from '../../interfaces/institution.interface';
import { AuthService } from '../../services/auth.service';
import { displayUsername } from '../../utils/userUtils';

interface User {
    id: string;
    username: string;
    phone: string;
    type: UserType;
    isActive: boolean;
}

const UserList = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<IInstitution | null>(null);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isTwoFactorResetModalOpen, setIsTwoFactorResetModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedType, setSelectedType] = useState<UserType | ''>('');
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const toast = useToast();
    const isSystemAdmin = AuthService.getCurrentUser()?.roles === 'SystemAdmin';
    const permissionsInstitutionId = selectedInstitution?.id ?? AuthService.getSelectedInstitution();
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [page,] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        if (isSystemAdmin) {
            fetchInstitutions();
        }
    }, [isAuthenticated, navigate, page]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchInstitutionUsers(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitution, debouncedSearch, isAuthenticated]);

    const fetchInstitutions = async () => {
        try {
            const response = await InstitutionService.getInstitutions(0, 20);
            setInstitutions(response.content || []);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load institutions');
        }
    };

    const fetchInstitutionUsers = async (page: number = 0) => {
        try {
            const response = await UserService.getInstitutionUsers(page, 10, selectedInstitution?.id, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (user: User) => {
        try {
            await UserService.toggleUserActive(user.id);
            toast.showSuccess(`User ${user.isActive !== false ? 'deactivated' : 'activated'} successfully`);
            await fetchInstitutionUsers(pageInfo.currentPage);
        } catch (error: any) {
            toast.showApiError(error, 'Failed to update user status');
        }
    };

    const handleOpenDeleteModal = (user: User) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await UserService.deleteUser(selectedUser.id);
            toast.showSuccess('User deleted successfully');
            await fetchInstitutionUsers(pageInfo.currentPage);
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.showApiError(error, 'Failed to delete user');
            handleCloseDeleteModal();
        }
    };

    const handleOpenTypeModal = (user: User) => {
        setSelectedUser(user);
        setSelectedType(user.type);
        setIsTypeModalOpen(true);
    };

    const handleCloseTypeModal = () => {
        setIsTypeModalOpen(false);
        setSelectedUser(null);
        setSelectedType('');
    };

    const handleOpenResetPasswordModal = (user: User) => {
        setSelectedUser(user);
        setIsResetPasswordModalOpen(true);
    };

    const handleCloseResetPasswordModal = () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
    };

    const handleTypeChange = async () => {
        if (!selectedUser || !selectedType) return;

        try {
            await UserService.updateUserType(selectedUser.id, selectedType);
            toast.showSuccess('User type updated successfully');
            await fetchInstitutionUsers();
            handleCloseTypeModal();
        } catch (error: any) {
            console.error('Error updating user type:', error);
            toast.showApiError(error, 'Failed to update user type');
            handleCloseTypeModal();
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;

        try {
            await UserService.resetUserPassword(selectedUser.id);
            toast.showSuccess('Password has been reset and sent to admin email. Please change password after login.');
            handleCloseResetPasswordModal();
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.showApiError(error, 'Failed to reset password');
        }
    };

    const handleOpenTwoFactorResetModal = (user: User) => {
        setSelectedUser(user);
        setIsTwoFactorResetModalOpen(true);
    };

    const handleCloseTwoFactorResetModal = () => {
        setIsTwoFactorResetModalOpen(false);
        setSelectedUser(null);
    };

    const handleResetTwoFactor = async () => {
        if (!selectedUser) return;
        try {
            await UserService.resetTwoFactor(selectedUser.id);
            toast.showSuccess('Two-factor authentication has been reset for this user');
            handleCloseTwoFactorResetModal();
        } catch (error) {
            toast.showApiError(error, 'Failed to reset two-factor authentication');
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchInstitutionUsers(page - 1); // API uses 0-based indexing
    };

    if (loading) return <div data-testid="loading-state">Loading...</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>User List</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/users/create')}
                >
                    Create User
                </Button>
            </div>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '20px' }}>
                {isSystemAdmin && (
                    <Autocomplete
                        options={institutions}
                        getOptionLabel={(option) => option.name}
                        value={selectedInstitution}
                        onChange={(_e, newValue) => setSelectedInstitution(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter by Institution" size="small" />
                        )}
                        sx={{ width: 300 }}
                    />
                )}
                <TextField
                    label="Search by username"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 260 }}
                />
                <span>Total Users: {pageInfo.totalElements}</span>
            </Box>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Username</th>
                        <th style={tableHeaderStyle}>Phone</th>
                        <th style={tableHeaderStyle}>Type</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.username}>
                            <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                            <td style={tableCellStyle}>{user.phone}</td>
                            <td style={tableCellStyle}>{user.type}</td>
                            <td style={tableCellStyle}>
                                <span style={{ color: user.isActive !== false ? 'green' : 'red' }}>
                                    {user.isActive !== false ? 'Active' : 'Inactive'}
                                </span>
                            </td>
                            <td style={tableCellStyle}>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={user.isActive !== false ? "Deactivate User" : "Activate User"} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleActive(user)}
                                            color={user.isActive !== false ? "success" : "default"}
                                            aria-label={user.isActive !== false ? "Deactivate User" : "Activate User"}
                                        >
                                            {user.isActive !== false ? <ToggleOn /> : <ToggleOff />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Change Type" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTypeModal(user)}
                                            color="primary"
                                            aria-label="Change Type"
                                        >
                                            <ManageAccounts />
                                        </IconButton>
                                    </Tooltip>
                                    {isSystemAdmin && (
                                        <Tooltip title="Delete User" arrow>
                                            <IconButton
                                                size="small"
                                                aria-label="Delete User"
                                                onClick={() => handleOpenDeleteModal(user)}
                                                color="error"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Reset Password" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenResetPasswordModal(user)}
                                            color="secondary"
                                            aria-label="Reset Password"
                                        >
                                            <LockReset />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reset Two-Factor Authentication" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTwoFactorResetModal(user)}
                                            color="primary"
                                            aria-label="Reset Two-Factor Authentication"
                                        >
                                            <PhonelinkLock />
                                        </IconButton>
                                    </Tooltip>
                                    {permissionsInstitutionId && (
                                        <Tooltip title="Manage Permissions" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/users/${user.id}/permissions?institutionId=${permissionsInstitutionId}`)}
                                                color="info"
                                                aria-label="Manage Permissions"
                                            >
                                                <Security />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-describedby="delete-user-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Delete User</h3>
                    <p>Are you sure you want to delete user "{displayUsername(selectedUser?.username)}"?</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Type Change Modal */}
            <Modal
                open={isTypeModalOpen}
                onClose={handleCloseTypeModal}
                aria-describedby="change-type-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Change User Type</h3>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>User Type</InputLabel>
                        <Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value as UserType)}
                            label="User Type"
                        >
                            {Object.values(UserType).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTypeModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleTypeChange} variant="contained" color="primary">
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Reset Password Modal */}
            <Modal
                open={isResetPasswordModalOpen}
                onClose={handleCloseResetPasswordModal}
                aria-describedby="reset-password-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Reset Password</h3>
                    <p>Are you sure you want to reset the password for user "{displayUsername(selectedUser?.username)}"?</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseResetPasswordModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleResetPassword} variant="contained" color="secondary">
                            Reset Password
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Two-Factor Reset Modal */}
            <Modal
                open={isTwoFactorResetModalOpen}
                onClose={handleCloseTwoFactorResetModal}
                aria-describedby="reset-2fa-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Reset Two-Factor Authentication</h3>
                    <p>
                        This will disable two-factor authentication for "{displayUsername(selectedUser?.username)}"
                        and delete their backup codes. They will need to set it up again.
                    </p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTwoFactorResetModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleResetTwoFactor} variant="contained" color="error">
                            Reset
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default UserList;
